import { useEffect, useState, type FormEvent } from 'react'
import { PageHeading } from '@/components/Layout.tsx'
import { LeafDivider, Seedling, Sprig } from '@/components/Botanical.tsx'
import { Plus, Trash } from '@/components/Icons.tsx'
import { api, ApiError } from '@/lib/api.ts'
import { longDate } from '@/lib/format.ts'
import type { Condition, Member, Question } from '@/lib/types.ts'

/** What would have to be true for this to feel right. */
function Conditions({ items, members, onChange }: {
  items: Condition[]
  members: Member[]
  onChange: (next: Condition[]) => void
}) {
  const [adding, setAdding] = useState(false)
  const met = items.filter((c) => c.metAt).length

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const text = String(new FormData(form).get('text') ?? '')
    const { condition } = await api.groundwork.addCondition(text)
    onChange([...items, condition])
    form.reset()
    setAdding(false)
  }

  async function toggle(condition: Condition) {
    const met = !condition.metAt
    onChange(items.map((c) => (c.id === condition.id ? { ...c, metAt: met ? new Date().toISOString() : undefined } : c)))
    try {
      const { condition: saved } = await api.groundwork.setMet(condition.id, met)
      onChange(items.map((c) => (c.id === condition.id ? saved : c)))
    } catch { /* the optimistic state is close enough to retry from */ }
  }

  async function remove(id: string) {
    onChange(items.filter((c) => c.id !== id))
    await api.groundwork.removeCondition(id).catch(() => undefined)
  }

  return (
    <section>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <p className="eyebrow">The list</p>
          <h2 className="mt-2 font-display text-[1.9rem]">What would have to be true</h2>
        </div>
        {items.length > 0 && (
          <span className="text-[0.76rem] text-whisper">{met} of {items.length} so far</span>
        )}
      </div>

      <p className="prose-soft mt-4 max-w-measure text-[0.95rem]">
        Not whether to. What would have to be true before it felt right. Much easier to answer, and
        the only honest way to see whether you are getting anywhere.
      </p>

      <LeafDivider tone="var(--c-sage)" className="mt-5 h-4 w-full opacity-35" />

      {items.length === 0 ? (
        <div className="mt-8 flex flex-col items-center gap-4 py-8 text-center">
          <Seedling tone="var(--c-sage)" className="h-14 w-auto opacity-50" />
          <p className="prose-soft max-w-[24rem] text-[0.92rem]">
            Nothing listed yet. They can be small — a conversation you have not had, a month of
            savings, a room you would need to clear.
          </p>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-linen">
          {items.map((condition) => {
            const by = members.find((m) => m.id === condition.metBy)
            return (
              <li key={condition.id} className="group flex items-start gap-3.5 py-3.5">
                <button
                  onClick={() => void toggle(condition)}
                  aria-pressed={Boolean(condition.metAt)}
                  aria-label={condition.metAt ? 'Mark as not yet true' : 'Mark as true'}
                  className={[
                    'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border transition-all duration-300',
                    condition.metAt ? 'border-moss bg-moss text-paper' : 'border-linen hover:border-sage',
                  ].join(' ')}
                >
                  {condition.metAt && <span className="text-[0.6rem] leading-none">✓</span>}
                </button>

                <span className="min-w-0 flex-1">
                  <span className={['block text-[1rem] leading-[1.5]', condition.metAt ? 'text-whisper line-through' : 'text-ink'].join(' ')}>
                    {condition.text}
                  </span>
                  {condition.metAt && (
                    <span className="mt-1 block text-[0.72rem] text-whisper">
                      true since {longDate(condition.metAt)}{by ? ` · ${by.displayName} said so` : ''}
                    </span>
                  )}
                </span>

                <button
                  onClick={() => void remove(condition.id)}
                  aria-label="Remove"
                  className="shrink-0 rounded-full p-1.5 text-whisper opacity-0 transition-all hover:text-terracotta focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <Trash className="h-3.5 w-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-6">
        {adding ? (
          <form onSubmit={add} className="flex max-w-measure gap-2">
            <input name="text" required autoFocus placeholder="It would help if…" className="field py-2.5 text-[0.95rem]" />
            <button type="submit" className="btn-primary shrink-0 py-2.5 text-[0.85rem]">Add</button>
          </form>
        ) : (
          <button onClick={() => setAdding(true)} className="btn-ghost py-2.5 text-[0.85rem]">
            <Plus className="h-4 w-4" />
            Add a condition
          </button>
        )}
      </div>
    </section>
  )
}

/** Questions worth putting to people who have already done this. */
function Questions({ items, onChange }: { items: Question[]; onChange: (next: Question[]) => void }) {
  const [adding, setAdding] = useState(false)
  const [answering, setAnswering] = useState<string | null>(null)

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const { question } = await api.groundwork.addQuestion(
      String(data.get('text') ?? ''),
      String(data.get('person') ?? ''),
    )
    onChange([...items, question])
    form.reset()
    setAdding(false)
  }

  async function record(event: FormEvent<HTMLFormElement>, question: Question) {
    event.preventDefault()
    const answer = String(new FormData(event.currentTarget).get('answer') ?? '')
    const { question: saved } = await api.groundwork.setAnswer(question.id, answer)
    onChange(items.map((q) => (q.id === question.id ? saved : q)))
    setAnswering(null)
  }

  return (
    <section className="mt-24">
      <p className="eyebrow">Worth asking</p>
      <h2 className="mt-2 font-display text-[1.9rem]">People who have already done it</h2>
      <p className="prose-soft mt-4 max-w-measure text-[0.95rem]">
        Strangers on the internet will tell you anything. The people who actually know you are a
        better source, and they are usually delighted to be asked.
      </p>

      <LeafDivider tone="var(--c-clay)" className="mt-5 h-4 w-full opacity-30" />

      {items.length === 0 ? (
        <div className="mt-8 flex flex-col items-center gap-4 py-8 text-center">
          <Sprig tone="var(--c-clay)" className="h-16 w-auto opacity-40" />
          <p className="prose-soft max-w-[24rem] text-[0.92rem]">
            Nothing logged yet. Write down the question while you are thinking of it, and who you would
            want to ask.
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-7">
          {items.map((question) => (
            <li key={question.id} className="group border-l-2 border-linen pl-5 transition-colors hover:border-clay">
              <p className="eyebrow">{question.person}</p>
              <p className="mt-2 font-display text-[1.2rem] leading-[1.4]">{question.text}</p>

              {question.answer ? (
                <p className="prose-soft mt-3 whitespace-pre-wrap text-[0.95rem] italic">{question.answer}</p>
              ) : answering === question.id ? (
                <form onSubmit={(e) => void record(e, question)} className="mt-3 space-y-2">
                  <textarea name="answer" rows={3} autoFocus placeholder="What did they say?" className="field resize-none text-[0.95rem]" />
                  <div className="flex gap-2">
                    <button type="submit" className="btn-primary py-2 text-[0.82rem]">Save it</button>
                    <button type="button" onClick={() => setAnswering(null)} className="btn-ghost py-2 text-[0.82rem]">Not yet</button>
                  </div>
                </form>
              ) : (
                <button onClick={() => setAnswering(question.id)} className="mt-3 text-[0.78rem] uppercase tracking-[0.16em] text-sage transition-colors hover:text-fern">
                  Write down what they said
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8">
        {adding ? (
          <form onSubmit={add} className="sheet max-w-measure space-y-3 p-5">
            <label className="block">
              <span className="eyebrow">Who</span>
              <input name="person" required autoFocus placeholder="Your brother, her friend from work…" className="field mt-1.5 py-2.5 text-[0.95rem]" />
            </label>
            <label className="block">
              <span className="eyebrow">What you'd ask</span>
              <textarea name="text" required rows={2} placeholder="The thing you would actually want to know" className="field mt-1.5 resize-none text-[0.95rem]" />
            </label>
            <div className="flex gap-2 pt-1">
              <button type="submit" className="btn-primary py-2 text-[0.85rem]">Add it</button>
              <button type="button" onClick={() => setAdding(false)} className="btn-ghost py-2 text-[0.85rem]">Never mind</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setAdding(true)} className="btn-ghost py-2.5 text-[0.85rem]">
            <Plus className="h-4 w-4" />
            Add a question
          </button>
        )}
      </div>
    </section>
  )
}

export default function Groundwork() {
  const [conditions, setConditions] = useState<Condition[]>([])
  const [questions, setQuestions] = useState<Question[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.groundwork
      .list()
      .then((d) => { setConditions(d.conditions); setQuestions(d.questions); setMembers(d.members) })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'This could not be opened.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <>
      <PageHeading eyebrow="The practical half" title="Groundwork">
        The parts of this that are not a feeling. What would need to change, and who could tell you
        something you do not know yet.
      </PageHeading>

      {error && <p className="mb-6 text-[0.88rem] text-terracotta">{error}</p>}
      {loading ? (
        <p className="text-whisper">Opening…</p>
      ) : (
        <>
          <Conditions items={conditions} members={members} onChange={setConditions} />
          <Questions items={questions} onChange={setQuestions} />
        </>
      )}

      <LeafDivider tone="var(--c-sage)" className="mx-auto mt-24 h-4 w-40 opacity-30" />
    </>
  )
}
